import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import { IconButton, MenuItem } from '@material-ui/core'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import DeleteIcon from '@material-ui/icons/Delete'
import { useState } from 'react'

import { PMS_TYPE_NAMES_QUERY } from '../../graphql/pmstypes'
import { gregorianToJalaaliWithHourMinuteAndSecond } from '../../helpers/date'
import { getTranslate, lang } from '../../localization'
import { PMSTypeName, ProviderAdapter } from '../../types/accommodation'
import { ProviderSubmit } from '../../types/accommodationProviderSubmit'
import useStyles from './Styles/ProviderARIRow.style'

interface ProviderARIRowProps {
  selectedProviderSubmit: ProviderSubmit[]
  setSelectedProviderSubmit: React.Dispatch<
    React.SetStateAction<ProviderSubmit[]>
  >
  selectedProviderSubmitIndex: number
  ariIndex: number
}

export default function ProviderARIRow({
  selectedProviderSubmit,
  setSelectedProviderSubmit,
  selectedProviderSubmitIndex,
  ariIndex
}: ProviderARIRowProps) {
  const classes = useStyles()
  const [pmsTypes, setPMSTypes] = useState<PMSTypeName[]>([])

  const {} = useQuery<{
    pmsTypeNames: PMSTypeName[]
  }>(PMS_TYPE_NAMES_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      setPMSTypes(res.pmsTypeNames)
    }
  })

  const currentRetrievals =
    selectedProviderSubmit[selectedProviderSubmitIndex].ARIRetrievals

  const disableIcon =
    !currentRetrievals[ariIndex].adapter ||
    !currentRetrievals[ariIndex].retrievalInterval

  const handleDeleteRow = (index: number) => {
    const updatedRetrievals = currentRetrievals.filter(
      (_item, i) => i !== index
    )
    setSelectedProviderSubmit((prev) => {
      const updatedProviderSubmit = [...prev]
      updatedProviderSubmit[selectedProviderSubmitIndex] = {
        ...updatedProviderSubmit[selectedProviderSubmitIndex],
        ARIRetrievals: updatedRetrievals
      }
      return updatedProviderSubmit
    })
  }

  const handleAddRow = () => {
    if (currentRetrievals.length >= 3) return // avoid adding more than 3
    const newRetrieval = {
      adapter: '' as ProviderAdapter,
      retrievalInterval: 1800,
      lastRetrievalAttempt: '-',
      nextRetrievalAttempt: '-'
    }
    setSelectedProviderSubmit((prev) => {
      const updatedProviderSubmit = [...prev]
      updatedProviderSubmit[selectedProviderSubmitIndex] = {
        ...updatedProviderSubmit[selectedProviderSubmitIndex],
        ARIRetrievals: [...currentRetrievals, newRetrieval]
      }
      return updatedProviderSubmit
    })
  }

  return (
    <div className={classes.root}>
      <div className={classes.inputProviderPull}>
        <SelectWithLabel
          value={
            selectedProviderSubmit[selectedProviderSubmitIndex].ARIRetrievals[
              ariIndex
            ].adapter
          }
          onChange={(e) =>
            setSelectedProviderSubmit([
              ...selectedProviderSubmit.slice(0, selectedProviderSubmitIndex),
              {
                ...selectedProviderSubmit[selectedProviderSubmitIndex],
                ARIRetrievals: [
                  ...selectedProviderSubmit[
                    selectedProviderSubmitIndex
                  ].ARIRetrievals.slice(0, ariIndex),
                  {
                    ...selectedProviderSubmit[selectedProviderSubmitIndex]
                      .ARIRetrievals[ariIndex],
                    adapter: e.target.value as ProviderAdapter
                  },
                  ...selectedProviderSubmit[
                    selectedProviderSubmitIndex
                  ].ARIRetrievals.slice(ariIndex + 1)
                ]
              },
              ...selectedProviderSubmit.slice(selectedProviderSubmitIndex + 1)
            ])
          }
          size="small"
          label={getTranslate('Provider Adapter')}
          fullWidth>
          {pmsTypes.map((item, index) => (
            <MenuItem
              key={index}
              value={item.EN}
              classes={{
                root: lang === 'fa' ? classes.menuItem : undefined
              }}>
              {lang === 'fa' ? item.FA : item.EN}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <InputWithLabel
          inputSize="small"
          label={getTranslate('Pull Interval (in minutes)')}
          fullWidth
          placeholder={getTranslate('Pull Interval')}
          autoComplete="off"
          value={currentRetrievals[ariIndex].retrievalInterval / 60}
          onChange={(e) => {
            const newValue = parseInt(e.currentTarget.value, 10)
            const newInterval = !isNaN(newValue) ? newValue * 60 : 0

            setSelectedProviderSubmit((prev) => {
              const updatedProviderSubmit = [...prev]
              const updatedRetrievals = [
                ...updatedProviderSubmit[selectedProviderSubmitIndex]
                  .ARIRetrievals
              ]
              updatedRetrievals[ariIndex] = {
                ...updatedRetrievals[ariIndex],
                retrievalInterval: newInterval
              }
              updatedProviderSubmit[selectedProviderSubmitIndex] = {
                ...updatedProviderSubmit[selectedProviderSubmitIndex],
                ARIRetrievals: updatedRetrievals
              }

              return updatedProviderSubmit
            })
          }}
        />
        <InputWithLabel
          disabled={true}
          inputSize="small"
          label={getTranslate('Last Interval')}
          fullWidth
          placeholder={getTranslate('Last Interval')}
          autoComplete="off"
          value={
            !currentRetrievals[ariIndex].lastRetrievalAttempt
              ? ''
              : lang === 'fa'
              ? gregorianToJalaaliWithHourMinuteAndSecond(
                  currentRetrievals[ariIndex].lastRetrievalAttempt || ''
                )
              : currentRetrievals[ariIndex].lastRetrievalAttempt
          }
        />
        <InputWithLabel
          disabled={true}
          inputSize="small"
          label={getTranslate('Next Interval')}
          fullWidth
          placeholder={getTranslate('Next Interval')}
          autoComplete="off"
          value={
            !currentRetrievals[ariIndex].nextRetrievalAttempt
              ? ''
              : lang === 'fa'
              ? gregorianToJalaaliWithHourMinuteAndSecond(
                  currentRetrievals[ariIndex].nextRetrievalAttempt || ''
                )
              : currentRetrievals[ariIndex].nextRetrievalAttempt
          }
        />
        <div className={classes.iconsSection}>
          {ariIndex === 0 && currentRetrievals.length === 1 && (
            <IconButton onClick={handleAddRow} disabled={disableIcon}>
              <AddCircleOutlineIcon
                color={disableIcon ? 'disabled' : 'secondary'}
              />
            </IconButton>
          )}
          {ariIndex > 0 && (
            <div className={classes.twoIconsSection}>
              {ariIndex === 1 && currentRetrievals.length === 2 && (
                <IconButton onClick={handleAddRow} disabled={disableIcon}>
                  <AddCircleOutlineIcon
                    color={disableIcon ? 'disabled' : 'secondary'}
                  />
                </IconButton>
              )}
              <IconButton onClick={() => handleDeleteRow(ariIndex)}>
                <DeleteIcon color="error" />
              </IconButton>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
